// Additional terms are typed as plain clauses (a heading + ordinary text). The
// tags e-Contract wants are generated here — nobody types HTML.

export const FIRST_CLAUSE = 9; // clauses 1–8 belong to the standard template

const escapeHtml = (text) => String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const lines = (text) => String(text || '').split('\n').map(l => l.trim()).filter(Boolean);

export const newClause = (over = {}) => ({ title: '', body: '', ...over });

/** items -> the HTML sent as additional_terms ("" when there is nothing to send). */
export function termsToHtml(items, start = FIRST_CLAUSE) {
  return items.map((item, index) => {
    const title = String(item.title || '').trim();
    const body = lines(item.body);
    if (!title && body.length === 0) return '';
    const n = start + index;
    // Several lines become numbered sub-clauses (9.1, 9.2 …); a single line stays plain.
    const paragraphs = body.map((line, k) => `<p>${escapeHtml(body.length > 1 ? `${n}.${k + 1} ${line}` : line)}</p>`);
    return `<h2>${escapeHtml(`ข้อ ${n}${title ? ` ${title}` : ''}`)}</h2>${paragraphs.join('')}`;
  }).join('');
}

/** Saved HTML -> clauses, so a reload (or an older HTML-typed draft) shows up as text. */
export function htmlToTerms(html) {
  if (!html || !String(html).trim()) return [];
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const items = [];
  let current = null;
  const ensure = () => { if (!current) { current = newClause(); items.push(current); } return current; };
  const addLines = (text) => {
    // drop the "9.1 " numbering we add ourselves so it is not doubled on the next save
    const cleaned = lines(text).map(l => l.replace(/^\d+(\.\d+)+\s+/, ''));
    if (cleaned.length === 0) return;
    const clause = ensure();
    clause.body = [clause.body, ...cleaned].filter(Boolean).join('\n');
  };

  doc.body.childNodes.forEach(node => {
    if (node.nodeType !== Node.ELEMENT_NODE) { addLines(node.textContent); return; }
    if (/^H[1-4]$/.test(node.tagName)) {
      current = newClause({ title: node.textContent.trim().replace(/^ข้อ\s*\d+\s*/, '') });
      items.push(current);
    } else if (node.tagName === 'OL' || node.tagName === 'UL') {
      node.querySelectorAll('li').forEach(li => addLines(li.textContent));
    } else {
      addLines(node.textContent);
    }
  });
  return items;
}

/** Blanks (___) the rep still has to fill in before a sample is usable. */
export const hasUnfilledBlanks = (items) => items.some(i => /_{3,}/.test(`${i.title} ${i.body}`));

// Ready-made wording. Numbers a lawyer or the contract owner should decide are
// left as ___ so they cannot be sent by accident.
export const TERM_SAMPLES = [
  {
    label: 'เงินมัดจำ',
    title: 'เงินมัดจำ',
    body: 'ผู้เช่าได้ชำระเงินมัดจำให้แก่ผู้ให้เช่าตามจำนวนที่ระบุในสัญญา ณ วันทำสัญญา\nผู้ให้เช่าจะคืนเงินมัดจำภายใน ___ วันทำการ นับจากวันที่ได้รับเครื่องคืนและตรวจสอบสภาพเครื่องเรียบร้อยแล้ว โดยหักค่าเสียหายหรือค่าใช้จ่ายค้างชำระ (ถ้ามี)'
  },
  {
    label: 'การใช้งานและดูแลรักษา',
    title: 'การใช้งานและการดูแลรักษา',
    body: 'ผู้เช่าต้องใช้เครื่องตามวัตถุประสงค์ที่ระบุในสัญญาและตามคู่มือการใช้งาน\nผู้เช่าต้องไม่นำเครื่องไปให้เช่าช่วง ย้ายสถานที่ติดตั้ง หรือดัดแปลงเครื่อง โดยไม่ได้รับความยินยอมเป็นลายลักษณ์อักษรจากผู้ให้เช่า\nหากเครื่องขัดข้อง ผู้เช่าต้องแจ้งผู้ให้เช่าทันที และต้องไม่ซ่อมแซมเครื่องด้วยตนเอง'
  },
  {
    label: 'ความเสียหายและสูญหาย',
    title: 'ความเสียหายและการสูญหาย',
    body: 'หากเครื่องเสียหายหรือสูญหายอันเกิดจากการใช้งานที่ไม่เหมาะสมหรือความประมาทของผู้เช่า ผู้เช่าตกลงรับผิดชอบค่าซ่อมแซม\nหากเครื่องสูญหายหรือเสียหายจนไม่สามารถซ่อมแซมได้ ผู้เช่าตกลงชดใช้เป็นมูลค่าเครื่องตามที่ระบุในสัญญา'
  },
  {
    label: 'ชำระเงินล่าช้า',
    title: 'การชำระเงินล่าช้า',
    body: 'หากผู้เช่าชำระค่าเช่าล่าช้ากว่ากำหนด ผู้ให้เช่ามีสิทธิ์คิดดอกเบี้ยในอัตราร้อยละ ___ ต่อเดือน\nผู้ให้เช่ามีสิทธิ์ระงับการให้บริการจนกว่าผู้เช่าจะชำระครบถ้วน'
  },
  {
    label: 'การบอกเลิกสัญญา',
    title: 'การบอกเลิกสัญญา',
    body: 'คู่สัญญาฝ่ายใดฝ่ายหนึ่งมีสิทธิ์บอกเลิกสัญญาก่อนครบกำหนดได้ โดยแจ้งเป็นลายลักษณ์อักษรล่วงหน้าไม่น้อยกว่า ___ วัน'
  },
  {
    label: 'การคืนเครื่อง',
    title: 'การคืนเครื่อง',
    body: 'เมื่อสัญญาสิ้นสุดหรือถูกยกเลิก ผู้เช่าต้องคืนเครื่องในสภาพเรียบร้อยภายใน ___ วัน\nผู้ให้เช่าจะตรวจสอบสภาพเครื่องร่วมกับผู้เช่า ณ วันที่รับคืน'
  }
];
